// src/components/trip/StorageTab.tsx
import React, { useState } from 'react';
import { 
  View, 
  Heading, 
  Text, 
  Collection, 
  Card, 
  Flex,
  Button,
  Image
} from '@aws-amplify/ui-react';
import { Trash2, ImageIcon } from 'lucide-react';
import StorageUsageCard from './StorageUsageCard';
import { formatFileSize, calculatePercentage } from '../../utils/formatters';
import { deletePhoto } from '../../services/tripPhotoActions';
import { TripRole } from '../../API';

interface StorageTabProps {
  photos: any[];
  photoUrls: { [key: string]: string };
  storageUsed: number;
  userRole: string | null;
  onPhotoDeleted: (photoIds: string[]) => void;
}

const StorageTab: React.FC<StorageTabProps> = ({
  photos,
  photoUrls,
  storageUsed,
  userRole,
  onPhotoDeleted
}) => {
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Check if user can edit
  const canEdit = userRole === TripRole.PARTICIPANT || userRole === 'CREATOR';

  // Get the list of photos sorted by file size
  const sortedPhotos = [...photos].sort((a, b) => (b.fileSize || 0) - (a.fileSize || 0));

  // Handler for deleting a large photo
  const handleDeletePhoto = async (photoId: string) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this photo? This action cannot be undone.'
    );

    if (!confirmDelete) return;

    try {
      setIsDeleting(photoId);
      await deletePhoto(photoId);
      onPhotoDeleted([photoId]);
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete the photo. Please try again.');
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <View>
      <Heading level={3} marginBottom="1.5rem">Storage Usage</Heading>

      {/* Overall Storage Card */}
      <StorageUsageCard
        storageUsed={storageUsed}
        photoCount={photos.length}
      />

      {/* Largest Photos Section */}
      <Heading level={4} marginBottom="1rem">Largest Photos</Heading>

      {photos.length === 0 ? (
        <View textAlign="center" padding="2rem" backgroundColor="#202020" borderRadius="medium">
          <Text fontSize="1rem">No photos in this trip yet.</Text>
        </View>
      ) : (
        <Collection
          type="list"
          items={sortedPhotos.slice(0, 5)} // Just show top 5 largest
          gap="0.5rem"
        >
          {(photo, index) => (
            <Card key={index} padding="1rem" backgroundColor="#202020" borderRadius="medium">
              <Flex direction="row" alignItems="center" gap="1rem">
                <View width="60px" height="60px" overflow="hidden" borderRadius="4px">
                  {photoUrls[photo.id] ? (
                    <Image
                      src={photoUrls[photo.id]}
                      alt={photo.fileName}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                    />
                  ) : (
                    <Flex height="100%" backgroundColor="#333" alignItems="center" justifyContent="center">
                      <ImageIcon size={24} color="#666" />
                    </Flex>
                  )}
                </View>

                <Flex direction="column" flex="1">
                  <Text fontWeight="medium" className="text-truncate">
                    {photo.fileName}
                  </Text>
                  <Text fontSize="0.8rem" color="#AAAAAA">
                    Uploaded by {photo.uploaderName}
                  </Text>
                </Flex>

                <Text fontWeight="bold">
                  {formatFileSize(photo.fileSize || 0)}
                </Text>

                {canEdit && (
                  <Button
                    variation="destructive"
                    size="small"
                    onClick={() => handleDeletePhoto(photo.id)}
                    isLoading={isDeleting === photo.id}
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </Flex>
            </Card>
          )}
        </Collection>
      )}
    </View>
  );
};

export default StorageTab;